/* eslint-disable import-helpers/order-imports */
import React from 'react';

import CardHeader from '@elements/CardHeader';
import CardBody from '@elements/CardBody';
import CardFooter from '@elements/CardFooter';
import CardVideo from '@elements/CardVideo';
import CardVideoNarrow from '@elements/CardVideoNarrow';
import CardFullTitle from '@elements/CardFullTitle';
import RichText from '@elements/RichText';
import { GatsbyImage, getImage, IGatsbyImageData } from "gatsby-plugin-image";

import './style.css';

type GenericCardProps = {
  item: {
    contentful_id: string;
    type: string;
    useFilter: boolean;
    extraClass: string;
    image: {
      title: string;
      gatsbyImageData: IGatsbyImageData;
    };
    body: {
      raw: string;
    };
  };
};

/**
 * @deprecated This component is deprecated and should no longer be used.
 * Please use `src/components/submodules/Card` instead.
 */
const GenericCard = ({ item }: GenericCardProps): JSX.Element => {
  const key = `card-${item.contentful_id}`;
  const extraClass = item?.extraClass || '';
  const classes = `card--${item.type} ${extraClass}`;
  const image = getImage(item.image?.gatsbyImageData);
  const imageTitle = item.image?.title;
  const content = item?.body;
  const label = content?.raw;
  const filter = item?.useFilter;

  switch (true) {
    case item.type?.startsWith('header'):
      return (
        <CardHeader key={`${key}-header`} childClassName={classes}>
          {content && <RichText bodyRichText={content} />}
        </CardHeader>
      );
    case item.type?.startsWith('footer'):
      return (
        <CardFooter key={`${key}-footer`} childClassName={classes}>
          {content && <RichText bodyRichText={content} />}
        </CardFooter>
      );
    case item.type?.startsWith('video'):
      return (
        <CardVideo
          key={`${key}-video`}
          content={item?.body}
        />
      );
    case item.type?.startsWith('premium-analysis'):
      return (
        <div className={extraClass}>
            <CardVideoNarrow
              key={`${key}-video`}
              content={item?.body}
            />
        </div>
      );
    case item.type?.includes('stakeholders-full-title'):
      return (
        <div className={extraClass}>
            <CardFullTitle
              key={`${key}-video`}
              content={item?.body}
            />
        </div>
      );
    case item.type?.endsWith('-esg-intelligence'):
      return (
        <div className={classes}>
          {
            image && (
              <div className='card-image'>
                <GatsbyImage
                  alt={imageTitle}
                  image={image}
                  imgStyle={{ objectFit: 'contain' }}
                />
              </div>
            )
          }
          {content && <RichText bodyRichText={content} />}
        </div>
      );
    case item.type?.endsWith('-esg-premium-analysis'):
      return (
        <div className={classes}>
          {
            image && (
              <div className='card-image'>
                <GatsbyImage
                  alt={imageTitle}
                  image={image}
                  imgStyle={{ objectFit: 'contain' }}
                />
              </div>
            )
          }
          {content && <RichText bodyRichText={content} />}
        </div>
      );
    case item.type?.endsWith('-stakeholders'):
    case item.type?.endsWith('-api'):
    case item.type?.endsWith('-intro-left'):
    case item.type?.endsWith('-set-list'):
    case item.type?.endsWith('-360'):
      return (
        <div className={classes}>
          {
            image && (
              <div className='card-image'>
                <GatsbyImage
                  alt={imageTitle}
                  image={image}
                  imgStyle={{ objectFit: 'contain' }}
                />
              </div>
            )
          }
          {content && <RichText bodyRichText={content} />}
        </div>
      );
      case item.type?.includes('upgrades'):
        return (
          <div className={classes}>
            {
              image && (
                <div className='card-image'>
                  <GatsbyImage
                    alt={imageTitle}
                    image={image}
                    imgStyle={{ objectFit: 'contain' }}
                  />
                </div>
              )
            }
            {content && <RichText bodyRichText={content} />}
          </div>
        );
    case label?.includes('labelTextAbove'):
      return (
        <div className={`card-image-side-container ${extraClass}`}>
          {content && <RichText bodyRichText={content} />}
          {
            image && (
              <div className="card-image-side">
                <GatsbyImage
                  alt={imageTitle}
                  image={image}
                />
              </div>
            )
          }
        </div>
      );
    case item.type?.startsWith('no-border-'):
    case item.type?.startsWith('bg-'):
    case item.type?.startsWith('bg-text'):
      return (
        <CardBody
          key={key}
          childClassName={classes}
          content={item?.body}
          image={image}
          imageTitle={imageTitle}
          useFilter={filter}
        />
      );
    case item.type?.includes('outcomes'):
      return (
        <div className={`card--outcomes-cards ${extraClass}`}>
          {
            image && (
              <figure>
                <GatsbyImage alt={imageTitle} image={image}/>
              </figure>
            )
          }
          {
            content && (
              <>
                <RichText bodyRichText={content}/>
              </>
            )
          }
        </div>
      );
    case item.type?.includes('image-bg-left'):
      return (
        <div className={`card--image-bg-left ${extraClass}`}>
          {
            content && (
              <>
                <RichText bodyRichText={content}/>
              </>
            )
          }
          {
            image && (
              <figure>
                <GatsbyImage alt={imageTitle} image={image}/>
              </figure>
            )
          }
        </div>
      );
    case item.type?.includes('image-bg-right'):
      return (
        <div className={`card--image-bg-right ${extraClass}`}>
          {
            image && (
              <figure>
                <GatsbyImage alt={imageTitle} image={image}/>
              </figure>
            )
          }
          {
            content && (
              <>
                <RichText bodyRichText={content}/>
              </>
            )
          }
        </div>
      );
    case item.type?.startsWith('bopa-image-bg-'):
      return (
        <div className={classes}>
          {image && <GatsbyImage alt={imageTitle} image={image}/>}
          {content && <RichText bodyRichText={content}/>}
        </div>
      );
    case item.type?.endsWith('business-footer'):
      return (
        <div className={`card--business-footer ${extraClass}`}>
          {
            content && (
              <>
                <RichText bodyRichText={content}/>
              </>
            )
          }
        </div>
      );
    default:
      return (
        <div className={extraClass}>
          {
            image && (
              <figure>
                <GatsbyImage alt={imageTitle} image={image}/>
              </figure>
            )
          }
          {
            content && (
              <>
                <RichText bodyRichText={content}/>
              </>
            )
          }
        </div>
      );
  }
};

export default GenericCard;